// src/services/apiService.js
import axios from 'axios';

export const TOKEN_CHANGE_EVENT = 'onsa-access-token-change';

const emitTokenChange = () => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    window.dispatchEvent(new Event(TOKEN_CHANGE_EVENT));
  } catch {
    try {
      if (typeof document !== 'undefined' && typeof document.createEvent === 'function') {
        const evt = document.createEvent('Event');
        evt.initEvent(TOKEN_CHANGE_EVENT, true, true);
        window.dispatchEvent(evt);
      }
    } catch {
      // ignore event dispatch failures
    }
  }
};

// Trim to avoid subtle bugs from trailing spaces in env (seen in production env files)
export const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8080').trim();
const BASE_URL = `${API_URL}/api`;
const REFRESH_ENDPOINT = '/auth/refresh'; // => POST { refresh_token }

// ---- Token helpers (localStorage version) ----
const getAccessToken  = () => localStorage.getItem('access_token');
const getRefreshToken = () => localStorage.getItem('refresh_token');
const saveTokens = ({ access_token, refresh_token }) => {
  let mutated = false;
  if (access_token) {
    localStorage.setItem('access_token', access_token);
    mutated = true;
  }
  if (refresh_token) {
    localStorage.setItem('refresh_token', refresh_token);
    mutated = true;
  }
  if (mutated) emitTokenChange();
};
const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  emitTokenChange();
};

// ---- Primary axios instance ----
const api = axios.create({ baseURL: BASE_URL, timeout: 15000 });

// ---- Separate instance used only for refresh (no interceptor to avoid loops) ----
const refreshApi = axios.create({ baseURL: BASE_URL, timeout: 15000 });

// ---- Attach Bearer token to every request ----
api.interceptors.request.use((config) => {
  if (config.skipAuth) return config;
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- On 401, try to refresh access token ----
let isRefreshing = false;
let refreshPromise = null;
const refreshSubscribers = [];
const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRefreshed = (newToken) => {
  while (refreshSubscribers.length) {
    const cb = refreshSubscribers.shift();
    try { cb(newToken); } catch { /* ignore */ }
  }
};

const refreshAccessToken = async () => {
  const rToken = getRefreshToken();
  if (!rToken) return null;
  const res = await refreshApi.post(REFRESH_ENDPOINT, { refresh_token: rToken });
  const { access_token, refresh_token } = res?.data || {};
  if (!access_token) return null;
  saveTokens({ access_token, refresh_token });
  return access_token;
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const code   = error?.response?.data?.error;
    const originalRequest = error?.config;

    // Reject immediately if there is no request config or credentials are invalid
    if (
      !originalRequest ||
      originalRequest.skipAuthRefresh ||
      (status === 401 && code === 'invalid_credentials')
    ) {
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If a refresh is already in flight, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            if (!newToken) return reject(error);
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${newToken}`,
            };
            resolve(api.request(originalRequest));
          });
        });
      }

      // Let this request initiate the refresh
      isRefreshing = true;
      try {
        if (!refreshPromise) refreshPromise = refreshAccessToken();
        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;
        onRefreshed(newToken);

        if (!newToken) {
          clearTokens();
          return Promise.reject(error);
        }

        // Retry the original request with the new token
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        return api.request(originalRequest);
      } catch (e) {
        isRefreshing = false;
        refreshPromise = null;
        onRefreshed(null);
        clearTokens();
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  }
);

// ---- Simple helpers (compatible with the previous API) ----
const apiService = {
  get:  async (url, params = {})     => (await api.get(url, { params })).data,
  post: async (url, data = {}, cfg)  => (await api.post(url, data, cfg)).data,
  put:  async (url, data = {}, cfg)  => (await api.put(url, data, cfg)).data,
  del:  async (url, cfg)             => (await api.delete(url, cfg)).data,

  // Expose token helpers for consumers that need direct access
  saveTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
};

export default apiService;
